// src/pages/Admin.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAdmin } from '../hooks/useAdmin';
import AdminLogin from '../components/Admin/AdminLogin';
import DashboardTab from '../components/Admin/DashboardTab';
import JobsManagement from '../components/Admin/JobsManagement';
import BlogManagement from '../components/Admin/BlogManagement';
import UsersManagement from '../components/Admin/UsersManagement';
import RecruiterManagement from '../components/Admin/RecruiterManagement';
import { getAllRecruiters } from '../services/recruiter';

const Admin = () => {
    const { currentUser } = useAuth();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [recruiters, setRecruiters] = useState([]);
    
    const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL;

    const {
        jobs,
        blogs,
        users,
        stats,
        loading,
        activeTab,
        setActiveTab,
        handleAddJob,
        handleUpdateJob,
        handleDeleteJob,
        handleAddBlog,
        handleUpdateBlog,
        handleDeleteBlog,
        handleDeleteUser,
        refreshData
    } = useAdmin();

    // Check if current logged-in user is admin
    const checkAdminAccess = () => {
        if (currentUser && currentUser.email === ADMIN_EMAIL) {
            setIsLoggedIn(true);
            return true;
        }
        return false;
    };

    // Load recruiters
    const loadRecruiters = async () => {
        const data = await getAllRecruiters();
        setRecruiters(data);
    };

    // Call this when component mounts and when currentUser changes
    useEffect(() => {
        checkAdminAccess();
        if (currentUser && currentUser.email === ADMIN_EMAIL) {
            loadRecruiters();
        }
    }, [currentUser]);

    const handleLogin = (status) => {
        setIsLoggedIn(status);
        if (status) {
            loadRecruiters();
        }
    };

    const refreshAllData = async () => {
        await refreshData();
        await loadRecruiters();
    };

    // No sessionStorage - always require fresh login
    if (!isLoggedIn) {
        return <AdminLogin onLogin={handleLogin} />;
    }

    if (loading) {
        return (
            <div className="flex justify-center items-center h-96">
                <i className="fas fa-spinner fa-spin text-4xl text-gradpath-green"></i>
                <span className="ml-3 text-gray-600">Loading dashboard...</span>
            </div>
        );
    }

    const tabs = [
        { id: 'dashboard', name: 'Dashboard', icon: 'fas fa-chart-line' },
        { id: 'jobs', name: 'Manage Jobs', icon: 'fas fa-briefcase' },
        { id: 'blog', name: 'Manage Blog', icon: 'fas fa-newspaper' },
        { id: 'users', name: 'User Records', icon: 'fas fa-users' },
        { id: 'recruiters', name: 'Recruiters', icon: 'fas fa-building' }
    ];

    return (
        <div className="container mx-auto px-4 py-8">
            {/* Admin Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gradpath-dark">Admin Dashboard</h1>
                    <p className="text-gray-500 text-sm">Manage jobs, blog posts, users, and recruiters</p>
                </div>
                <button
                    onClick={() => setIsLoggedIn(false)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-2"
                >
                    <i className="fas fa-sign-out-alt"></i> Logout
                </button>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-2 border-b border-gray-200 mb-6">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-4 py-2 font-medium transition-all flex items-center gap-2 ${
                            activeTab === tab.id
                                ? 'text-gradpath-green border-b-2 border-gradpath-green'
                                : 'text-gray-600 hover:text-gradpath-green'
                        }`}
                    >
                        <i className={tab.icon}></i>
                        {tab.name}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <div className="mt-6">
                {activeTab === 'dashboard' && (
                    <DashboardTab 
                        stats={stats} 
                        users={users} 
                        jobs={jobs}
                        recruiters={recruiters}
                        blogs={blogs}
                    />
                )}
                
                {activeTab === 'jobs' && (
                    <JobsManagement
                        jobs={jobs}
                        onDeleteJob={handleDeleteJob}
                        onUpdateJob={handleUpdateJob}
                        onAddJob={handleAddJob}
                    />
                )}
                
                {activeTab === 'blog' && (
                    <BlogManagement
                        blogs={blogs}
                        onDeleteBlog={handleDeleteBlog}
                        onUpdateBlog={handleUpdateBlog}
                        onAddBlog={handleAddBlog}
                    />
                )}
                
                {activeTab === 'users' && (
                    <UsersManagement
                        users={users}
                        onDeleteUser={handleDeleteUser}
                    />
                )}

                {activeTab === 'recruiters' && (
                    <RecruiterManagement
                        recruiters={recruiters}
                        onRefresh={refreshAllData}
                    />
                )}
            </div>
        </div>
    );
};

export default Admin;